import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Legend } from 'recharts';
import { TrendDataPoint } from '@/api/analytics';
import { format } from 'date-fns';
import { useMemo } from 'react';

interface ActionTrendChartProps {
  data: TrendDataPoint[];
}

const chartConfig = {
  created: {
    label: 'Created',
    color: 'hsl(var(--chart-1))',
  },
  completed: {
    label: 'Completed',
    color: 'hsl(var(--chart-5))',
  },
};

// Convert ISO week format (2025-W03) to readable date
const formatWeekLabel = (weekStr: string): string => {
  try {
    // Parse "2025-W03" into a date (first day of that week)
    const parts = weekStr.match(/(\d{4})-W(\d{2})/);
    if (!parts) return weekStr;
    
    const year = parseInt(parts[1]);
    const week = parseInt(parts[2]);
    
    // Calculate the date of the first day of this ISO week
    const jan4 = new Date(year, 0, 4);
    const weekStart = new Date(jan4);
    weekStart.setDate(jan4.getDate() - (jan4.getDay() || 7) + 1 + (week - 1) * 7);
    
    // Format as "MMM d" (e.g., "Jan 15")
    return format(weekStart, 'MMM d');
  } catch {
    return weekStr;
  }
};

export function ActionTrendChart({ data }: ActionTrendChartProps) {
  // Transform data to include formatted labels
  const formattedData = useMemo(() => {
    return data.map(item => ({
      ...item,
      weekLabel: formatWeekLabel(item.week),
      weekOriginal: item.week
    }));
  }, [data]);

  if (data.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Action Trends</CardTitle>
          <CardDescription>Actions created vs completed over time</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-[300px] text-muted-foreground">
            No data available
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Action Trends</CardTitle>
        <CardDescription>Actions created vs completed over time</CardDescription>
      </CardHeader>
      <CardContent>
        <ChartContainer config={chartConfig} className="h-[300px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={formattedData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
              <XAxis 
                dataKey="weekLabel" 
                tick={{ fontSize: 11 }}
                angle={-45}
                textAnchor="end"
                height={60}
              />
              <YAxis tick={{ fontSize: 12 }} width={40} />
              <ChartTooltip content={<ChartTooltipContent />} />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="created" 
                stroke="var(--color-created)" 
                strokeWidth={2}
                name="Created"
                dot={{ fill: 'var(--color-created)', r: 3 }}
              />
              <Line 
                type="monotone" 
                dataKey="completed" 
                stroke="var(--color-completed)" 
                strokeWidth={2}
                name="Completed"
                dot={{ fill: 'var(--color-completed)', r: 3 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </ChartContainer>
      </CardContent>
    </Card>
  );
}
